import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_CONTENT_LOCALE,
  type Course,
  type CourseMetadataTranslationPatch,
  type CourseMetadataTranslationProjection,
  type LocaleOption,
} from '@course-authoring/shared-types';
import { fetchWithAuthRetry, parseApiError, type ApiError } from './apiUtils';
import { contentLocaleKeys } from './contentLocaleApi';
import { courseRecordKeys, lessonUsageKeys, withEditedCoursePublishState } from './courseRecordApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const courseMetadataKeys = {
  all: ['courseMetadata'] as const,
  details: () => [...courseMetadataKeys.all, 'detail'] as const,
  detail: (courseId: string, locale = DEFAULT_CONTENT_LOCALE, accountId: number | null = null) =>
    [...courseMetadataKeys.details(), courseId, locale, accountId ?? 'base'] as const,
};

function metadataUrl(courseId: string, locale: string, accountId: number | null): string {
  const params = new URLSearchParams({ locale });
  if (accountId !== null) params.set('accountId', String(accountId));
  return `${API_BASE_URL}/courses/${courseId}/metadata?${params}`;
}

export async function fetchCourseMetadata(
  courseId: string,
  locale = DEFAULT_CONTENT_LOCALE,
  accountId: number | null = null,
): Promise<CourseMetadataTranslationProjection> {
  const response = await fetchWithAuthRetry(metadataUrl(courseId, locale, accountId));
  if (!response.ok) throw await parseApiError(response, 'Failed to fetch course metadata');
  return response.json();
}

export async function patchCourseMetadata(
  courseId: string,
  patch: CourseMetadataTranslationPatch,
  locale = DEFAULT_CONTENT_LOCALE,
  accountId: number | null = null,
): Promise<CourseMetadataTranslationProjection> {
  const response = await fetchWithAuthRetry(metadataUrl(courseId, locale, accountId), {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw await parseApiError(response, 'Failed to save course metadata');
  return response.json();
}

export function useCourseMetadataQuery(
  courseId: string,
  options?: { enabled?: boolean; locale?: string; accountId?: number | null },
) {
  const locale = options?.locale ?? DEFAULT_CONTENT_LOCALE;
  const accountId = options?.accountId ?? null;
  return useQuery({
    queryKey: courseMetadataKeys.detail(courseId, locale, accountId),
    queryFn: () => fetchCourseMetadata(courseId, locale, accountId),
    enabled: options?.enabled ?? Boolean(courseId),
    // Metadata editability and provenance are scope-specific. Retaining the
    // previous account's projection while the next request loads can briefly
    // expose the wrong values and editing permissions.
    placeholderData: undefined,
    staleTime: 0,
    retry: (failureCount, error: unknown) =>
      Boolean(error && typeof error === 'object' && 'retryable' in error && (error as ApiError).retryable)
      && failureCount < 3,
  });
}

export function useCourseMetadataMutation(
  courseId: string,
  options?: {
    locale?: string;
    accountId?: number | null;
    onSuccess?: (data: CourseMetadataTranslationProjection) => void;
    onError?: (error: ApiError) => void;
  },
) {
  const queryClient = useQueryClient();
  const locale = options?.locale ?? DEFAULT_CONTENT_LOCALE;
  const accountId = options?.accountId ?? null;
  return useMutation({
    mutationFn: ({
      patch,
      locale: requestLocale,
      accountId: requestAccountId,
    }: {
      patch: CourseMetadataTranslationPatch;
      locale?: string;
      accountId?: number | null;
    }) => patchCourseMetadata(
      courseId,
      patch,
      requestLocale ?? locale,
      requestAccountId ?? accountId,
    ),
    onSuccess: (data, request) => {
      const savedLocale = request.locale ?? locale;
      const savedAccountId = request.accountId ?? accountId;
      queryClient.setQueryData(
        courseMetadataKeys.detail(courseId, savedLocale, savedAccountId),
        data,
      );
      const localeOption = queryClient
        .getQueryData<LocaleOption[]>(contentLocaleKeys.all)
        ?.find((option) => option.locale === savedLocale)
        ?? { locale: savedLocale, label: savedLocale };
      const detailKey = courseRecordKeys.detail(courseId, savedLocale, savedAccountId);
      queryClient.setQueryData<Course>(detailKey, (previous) => previous
        ? {
            ...previous,
            title: data.title,
            description: data.description,
            certificateMessage: data.certificateMessage,
            completedMessage: data.completedMessage,
            availableLocales: mergeLocaleOption(previous.availableLocales, localeOption),
            // NA-296: force it now rather than wait for the invalidation below.
            publishState: withEditedCoursePublishState(previous),
          }
        : previous);
      void queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.removeQueries({
        queryKey: ['courseLearnerPreview', courseId, savedLocale, savedAccountId ?? 'base'],
      });
      if (savedAccountId === null) {
        queryClient.removeQueries({
          queryKey: [...courseMetadataKeys.details(), courseId],
          type: 'inactive',
        });
      }
      // NA-284: base default-locale titles render in the lessons "Used in
      // Courses" panel. Remove (don't invalidate) the cached usage rows: the
      // title is known-changed, and removal makes the panel's next open show
      // a loading state instead of flashing the old title while refetching.
      // Only title edits evict — the panel shows nothing else from metadata.
      if (savedAccountId === null && savedLocale === DEFAULT_CONTENT_LOCALE && 'title' in request.patch) {
        queryClient.removeQueries({ queryKey: lessonUsageKeys.all });
      }
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      const apiError: ApiError = error && typeof error === 'object' && 'status' in error
        ? (error as ApiError)
        : {
            status: 0,
            code: 'NETWORK_ERROR',
            message: error instanceof Error ? error.message : 'Network error',
            retryable: true,
          };
      options?.onError?.(apiError);
    },
    retry: false,
  });
}

function mergeLocaleOption(
  options: LocaleOption[] | undefined,
  localeOption: LocaleOption,
): LocaleOption[] {
  const existing = options ?? [];
  return existing.some((option) => option.locale === localeOption.locale)
    ? existing
    : [...existing, localeOption];
}
